/**
 * Web-side voice-note sync — closes the second half of SYNC-W1.
 *
 * Wire shape (mobile/src/sync/syncVoiceNotes.ts):
 *
 *   Voice-note METADATA travels INSIDE the Work blob's
 *   `voiceNotes` array (id, durationMs, recordedAt, name, …).
 *   Mobile broadcasts a Work upsert whenever a voice note is
 *   recorded; web's `applySyncBlob` already persists the work.
 *   This module hooks AFTER that save: for every voice note the
 *   work carries, persist a row in the `voiceNotes` IndexedDB
 *   store and lazily fetch the audio binary from the relay.
 *
 *   The audio BINARY uses the same /photos/:id endpoint the
 *   photos do — the relay treats blobs as opaque ciphertext, so
 *   mobile encodes the relay id as `audio_${noteId}`. Same wire
 *   format (`ivHex:base64(ct)`) where decrypting yields a base64
 *   of the raw audio bytes.
 *
 * Why patch via Blob URL instead of streaming the encrypted blob
 * straight to <audio>: the audio element doesn't decrypt, and we
 * need the AES-CBC pass anyway. Once we have the bytes a Blob URL
 * is the cheapest plug into the existing `<audio src>` render.
 *
 * Async fan-out: a Work upsert with N voice notes triggers N
 * concurrent fetches. Each fetch is independent and short — the
 * audio files are already capped at a few hundred KB by the
 * mobile recorder. We don't await them in `applyWorkVoiceNotes`
 * so the live-sync handler returns immediately and other sync
 * traffic isn't blocked.
 */
import { downloadEncryptedBlobUrl } from './encryptedBinary';
import { saveVoiceNote, getVoiceNote } from '../store/db';
import type { Work } from '../types';

const VOICE_MIME = 'audio/mp4';

export async function downloadVoiceNoteBlobUrl(
  noteId: string,
  teamKeyHex: string,
  companyToken: string,
): Promise<string | null> {
  // Mobile prefixes the relay id with `audio_` to avoid collisions
  // with photo ids in the same /photos/:id namespace.
  return downloadEncryptedBlobUrl(`audio_${noteId}`, teamKeyHex, companyToken, VOICE_MIME);
}

/**
 * Persist every voice note carried in `work.voiceNotes` and fire
 * the audio fetch for the ones missing a localPath. Idempotent:
 * a row already in IDB with a localPath is left alone, so a
 * second sync of the same Work doesn't re-download.
 *
 * Errors are swallowed — a single bad voice note shouldn't stop
 * the rest of the work from rendering.
 */
export async function applyWorkVoiceNotes(
  work: Work,
  teamKeyHex: string,
  companyToken: string,
): Promise<void> {
  const notes = work.voiceNotes ?? [];
  if (notes.length === 0) return;
  for (const note of notes) {
    try {
      // Mobile's localPath is a device-specific URI; useless on
      // the web. Strip it and start with empty metadata.
      const seedRow = {
        id: note.id,
        workId: work.id,
        name: (note as { name?: string }).name,
        durationMs: note.durationMs,
        recordedAt: note.recordedAt,
        localPath: '',
      };
      const existing = await getVoiceNote(note.id);
      if (existing && existing.localPath) {
        // Already downloaded — skip the fetch and refresh the
        // metadata in case something changed (rename, etc.).
        await saveVoiceNote({ ...seedRow, localPath: existing.localPath });
        continue;
      }
      await saveVoiceNote(seedRow);
      // Fire the fetch but don't await it; once the Blob URL is
      // ready we patch the row and the WorkDetail subscription
      // refetches getWorkVoiceNotes via the store-change emitter.
      void (async () => {
        const url = await downloadVoiceNoteBlobUrl(note.id, teamKeyHex, companyToken);
        if (!url) return;
        try {
          await saveVoiceNote({ ...seedRow, localPath: url });
        } catch {
          URL.revokeObjectURL(url);
        }
      })();
    } catch {
      // Per-note failure shouldn't break the loop.
    }
  }
}
